// Cómo se llama de cara al alumno lo que el servidor manda como clave.
//
//     node scripts/prueba_puertas.mjs        (desde frontend/)
//
// PARA QUÉ HACE FALTA ESTO. Desde que el mundo se abre por niveles, el servidor contesta
// *«esta puerta pide 2 lecciones de `cultura-clasica`»*, y ese guión con esa falta de acentos
// es una clave de base de datos, no una frase. El cartel tiene que decir un nombre, y la razón
// está escrita en `backend/app/domain/niveles.py`: «te falta 1 lección» manda a estudiar a
// ciegas; «te falta 1 lección de Cultura y política» manda a estudiar.
//
// Y EL NOMBRE ES LA COMPETENCIA, QUE ES EL CAMBIO DE W13. Hasta el 14 de octubre de 2026 esto
// se llamaba `materias.mjs` y decía «Cultura Clásica», que es el `name` de `courses.js`. El
// glosario de la casa dice otra cosa con todas las letras: **materia es la asignatura del
// instituto** —Latín, Lengua y Literatura Castellanas— y es una etiqueta de cada curso, no un
// nivel; lo que el mapa de la Academia usa para organizarse son **las cinco competencias**. Un
// cartel que decía «te faltan 2 lecciones de Cultura Clásica» estaba nombrando con la palabra
// del instituto algo que en esta casa se llama de otra manera, y Gabri lo dejó apuntado el 8 de
// octubre: *«si nos quedamos con palabras heredadas costará avanzar»*.
//
// LA CLAVE NO SE TOCA, y es media regla de la casa: `cultura-clasica` sigue siendo
// `cultura-clasica` en el servidor, en las rutas y en los `slug`. **De la pantalla para fuera,
// el nombre; de la pantalla para dentro, la clave.**
//
// Y POR QUÉ NO SE LEE DE `data/courses.js`, QUE ES DONDE VIVE. Porque aquel fichero importa las
// cinco portadas —imágenes— y eso lo resuelve webpack y no `node`: cualquier `.mjs` que lo
// importara dejaría de poder probarse sin levantar el navegador, que es justo lo que esta casa
// no hace. Así que aquí hay una copia de cinco líneas.
//
// LA COPIA ESTÁ ATADA AL ORIGINAL, que es lo único que la hace aceptable. `prueba_puertas.mjs`
// abre `data/courses.js` como texto y comprueba que los cinco `slug` y las cinco `competencia`
// son exactamente éstos: el día que alguien renombre una competencia allí, esto se pone rojo en
// vez de quedarse diciendo el nombre viejo durante meses. Una copia que nadie vigila se separa
// del original; una copia vigilada es una lectura con otro nombre.

/** Las cinco competencias Alpha: clave del servidor → nombre de cara al alumno. */
export const COMPETENCIAS = {
  filosofia: "Pensamiento crítico",
  literatura: "Comunicación",
  psicologia: "Autocomprensión",
  "cultura-clasica": "Cultura y política",
  musica: "Creatividad",
};

/**
 * EL COLOR DE CADA COMPETENCIA, en clases enteras y no en trozos.
 *
 * POR QUE ENTERAS Y NO `bg-alpha-${x}`. Porque Tailwind no ejecuta el codigo: lee los
 * ficheros buscando clases escritas, y una clase construida al vuelo **no la ve**, asi que no
 * genera el CSS y el color no sale. Es el mismo silencio que ya le costo a esta casa
 * setecientas clases mudas, contado en `tailwind.config.js`. Verboso a cambio de que pinte.
 *
 * Y LAS VARIANTES VAN ESCRITAS TAMBIEN. `hover:${color.borde}` parece lo mismo y no lo es:
 * la clase que acaba en el HTML es `hover:border-alpha-critico`, y esa cadena no esta escrita
 * en ningun fichero, asi que Tailwind tampoco la genera. El `hover` no hacia nada. Por eso
 * cada competencia trae su reposo **y** su variante, las dos enteras y las dos legibles por
 * el escaner.
 *
 * Y LA SOMBRA SE LLAMA LUZ desde el 17 de octubre de 2026, que no es un capricho de nombre:
 * Gabri pidio *«que emita luz al poner el cursor encima»*, y una sombra del color de la
 * competencia al 45 % debajo de una tarjeta blanca **no se lee como sombra, se lee como un
 * halo**. El nombre de la pieza dice lo que hace en pantalla, no como esta hecha. Subio tambien
 * de 25 a 45: al 25 el halo existia y no se veia, que es la peor de las dos cosas.
 *
 * DONDE VIVE EL COLOR, que es la decision de fondo: en reposo, solo en **la banda de arriba y
 * la etiqueta de etapa**. La tarjeta sigue blanca y el texto negro. Ocho cursos juntos con el
 * fondo de su color son ocho colores peleandose; con una banda, se leen como una familia. El
 * color pleno es para cuando el alumno toca algo — igual que el rojo del cielo de la Estrella
 * es un momento y no un estado.
 *
 * `ventana`, `mensaje` y `rotulo` SON EL CHAT DE LA LECCION: el unico sitio donde el color llena
 * una superficie, porque ahi no hay ocho colores peleandose sino uno solo, el de esa leccion.
 * `preguntas` es lo mismo al reves, para la fase de Aplicar: `leccion/preguntasDelChat.css`.
 */
export const COLOR_DE_LA_COMPETENCIA = {
  filosofia: {
    banda: "bg-alpha-critico", suave: "bg-alpha-critico/25", borde: "border-alpha-critico",
    texto: "text-alpha-critico",
    preguntas: "bg-alpha-critico-oscuro [--claro:theme(colors.alpha.critico-claro)] [--oscuro:theme(colors.alpha.critico-oscuro)]",
    ventana: "bg-alpha-critico-claro", mensaje: "bg-alpha-critico-oscuro", rotulo: "text-alpha-critico-oscuro",
    bordeAlPasar: "hover:border-alpha-critico", luzAlPasar: "hover:shadow-alpha-critico/45",
    textoAlPasar: "group-hover:text-alpha-critico", bandaAlPasar: "group-hover:bg-alpha-critico",
  },
  literatura: {
    banda: "bg-alpha-comunicacion", suave: "bg-alpha-comunicacion/25", borde: "border-alpha-comunicacion",
    texto: "text-alpha-comunicacion",
    preguntas: "bg-alpha-comunicacion-oscuro [--claro:theme(colors.alpha.comunicacion-claro)] [--oscuro:theme(colors.alpha.comunicacion-oscuro)]",
    ventana: "bg-alpha-comunicacion-claro", mensaje: "bg-alpha-comunicacion-oscuro", rotulo: "text-alpha-comunicacion-oscuro",
    bordeAlPasar: "hover:border-alpha-comunicacion", luzAlPasar: "hover:shadow-alpha-comunicacion/45",
    textoAlPasar: "group-hover:text-alpha-comunicacion", bandaAlPasar: "group-hover:bg-alpha-comunicacion",
  },
  psicologia: {
    banda: "bg-alpha-autocomprension", suave: "bg-alpha-autocomprension/25", borde: "border-alpha-autocomprension",
    texto: "text-alpha-autocomprension",
    preguntas: "bg-alpha-autocomprension-oscuro [--claro:theme(colors.alpha.autocomprension-claro)] [--oscuro:theme(colors.alpha.autocomprension-oscuro)]",
    ventana: "bg-alpha-autocomprension-claro", mensaje: "bg-alpha-autocomprension-oscuro", rotulo: "text-alpha-autocomprension-oscuro",
    bordeAlPasar: "hover:border-alpha-autocomprension", luzAlPasar: "hover:shadow-alpha-autocomprension/45",
    textoAlPasar: "group-hover:text-alpha-autocomprension", bandaAlPasar: "group-hover:bg-alpha-autocomprension",
  },
  "cultura-clasica": {
    banda: "bg-alpha-cultura", suave: "bg-alpha-cultura/25", borde: "border-alpha-cultura",
    texto: "text-alpha-cultura",
    preguntas: "bg-alpha-cultura-oscuro [--claro:theme(colors.alpha.cultura-claro)] [--oscuro:theme(colors.alpha.cultura-oscuro)]",
    ventana: "bg-alpha-cultura-claro", mensaje: "bg-alpha-cultura-oscuro", rotulo: "text-alpha-cultura-oscuro",
    bordeAlPasar: "hover:border-alpha-cultura", luzAlPasar: "hover:shadow-alpha-cultura/45",
    textoAlPasar: "group-hover:text-alpha-cultura", bandaAlPasar: "group-hover:bg-alpha-cultura",
  },
  musica: {
    banda: "bg-alpha-creatividad", suave: "bg-alpha-creatividad/25", borde: "border-alpha-creatividad",
    texto: "text-alpha-creatividad",
    preguntas: "bg-alpha-creatividad-oscuro [--claro:theme(colors.alpha.creatividad-claro)] [--oscuro:theme(colors.alpha.creatividad-oscuro)]",
    ventana: "bg-alpha-creatividad-claro", mensaje: "bg-alpha-creatividad-oscuro", rotulo: "text-alpha-creatividad-oscuro",
    bordeAlPasar: "hover:border-alpha-creatividad", luzAlPasar: "hover:shadow-alpha-creatividad/45",
    textoAlPasar: "group-hover:text-alpha-creatividad", bandaAlPasar: "group-hover:bg-alpha-creatividad",
  },
};

/** Negro cuando la competencia no se conoce: un curso nuevo sale sobrio, no sin pintar. */
export const SIN_COLOR = {
  banda: "bg-alpha-black", suave: "bg-alpha-black/20", borde: "border-alpha-black",
  texto: "text-alpha-black",
  preguntas: "bg-alpha-black [--claro:theme(colors.alpha.off)] [--oscuro:theme(colors.alpha.black)]",
  ventana: "bg-alpha-off", mensaje: "bg-alpha-black", rotulo: "text-alpha-black",
  bordeAlPasar: "hover:border-alpha-black", luzAlPasar: "hover:shadow-alpha-black/40",
  textoAlPasar: "group-hover:text-alpha-black", bandaAlPasar: "group-hover:bg-alpha-black",
};

/** El color de una competencia por su clave de servidor. Nunca devuelve nada vacio. */
export const colorDeLaCompetencia = (clave) => COLOR_DE_LA_COMPETENCIA[clave] || SIN_COLOR;

/**
 * EL NOMBRE DE UNA COMPETENCIA, y si no se conoce, la clave tal cual.
 *
 * Devolver la clave y no una cadena vacía es a propósito: el día que el servidor cierre una
 * puerta con una clave que aquí no está —una recién publicada—, el cartel dirá «te faltan 2
 * lecciones de historia-del-arte», que es feo pero cierto. Callar el nombre diría «te faltan 2
 * lecciones» a secas, que es la frase que manda a estudiar a ciegas.
 */
export const nombreDeLaCompetencia = (clave) => COMPETENCIAS[clave] || String(clave || "");

export default nombreDeLaCompetencia;
